import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "gashi_consent_preferences";
const COOKIE_NAME = "gashi_consent_preferences";
const defaultConsent = { essential: true, statistics: false, marketing: false };

const ConsentContext = createContext(null);

const setConsentCookie = (value) => {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=${60 * 60 * 24 * 180}; samesite=lax; secure`;
};

const getStoredConsent = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export const useConsent = () => useContext(ConsentContext);

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(defaultConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setConsent({ ...defaultConsent, ...stored });
      setConsentCookie({ ...defaultConsent, ...stored });
    } else {
      setShowBanner(true);
    }
  }, []);

  const logConsent = useCallback(async (action, nextConsent) => {
    try {
      await axios.post(`${API}/consent/log`, {
        consent_id: crypto.randomUUID(),
        action,
        source: showSettings ? "footer" : "banner",
        categories: nextConsent,
      });
    } catch {}
  }, [showSettings]);

  const persistConsent = useCallback(async (action, nextConsent) => {
    const normalized = { essential: true, statistics: !!nextConsent.statistics, marketing: !!nextConsent.marketing };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    setConsentCookie(normalized);
    setConsent(normalized);
    setShowBanner(false);
    setShowSettings(false);
    await logConsent(action, normalized);
  }, [logConsent]);

  const revokeConsent = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; samesite=lax; secure`;
    setConsent(defaultConsent);
    setShowBanner(true);
    setShowSettings(false);
    await logConsent("revoke", defaultConsent);
  }, [logConsent]);

  const value = useMemo(() => ({
    consent,
    showBanner,
    showSettings,
    setShowSettings,
    openConsentSettings: () => setShowSettings(true),
    acceptAll: () => persistConsent("accept_all", { essential: true, statistics: true, marketing: true }),
    rejectOptional: () => persistConsent("reject_optional", defaultConsent),
    saveConsent: (nextConsent) => persistConsent("save_preferences", nextConsent),
    revokeConsent,
  }), [consent, showBanner, showSettings, persistConsent, revokeConsent]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const ConsentBanner = () => {
  const { consent, showBanner, showSettings, setShowSettings, acceptAll, rejectOptional, saveConsent } = useConsent();
  const [draft, setDraft] = useState(consent);

  useEffect(() => setDraft(consent), [consent, showSettings]);

  if (!showBanner && !showSettings) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[120] p-4" data-testid="consent-banner">
      <div className="mx-auto max-w-4xl rounded-2xl border border-[#0A2A43]/20 bg-white shadow-2xl">
        <div className="p-5 md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="max-w-2xl">
              <h3 className="text-lg font-bold text-[#0A2A43] font-barlow">Cookie-Einstellungen</h3>
              <p className="mt-2 text-sm text-[#475569]">Wir verwenden Cookies zur Verbesserung der Website. Tracking erfolgt nur nach Ihrer Zustimmung. Einstellungen verwalten.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={rejectOptional} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-[#0A2A43] hover:bg-gray-50" data-testid="consent-reject-button">Nur essenziell</button>
              <button type="button" onClick={acceptAll} className="px-4 py-2 rounded-lg bg-[#0A2A43] text-white text-sm hover:bg-[#0A2A43]/90" data-testid="consent-accept-button">Alle akzeptieren</button>
              <button type="button" onClick={() => setShowSettings(!showSettings)} className="px-4 py-2 rounded-lg bg-[#D32F2F] text-white text-sm hover:bg-[#D32F2F]/90" data-testid="consent-settings-button">Einstellungen</button>
            </div>
          </div>

          {showSettings && (
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              <label className="rounded-xl border p-4 bg-gray-50">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0A2A43]">Essentiell</p>
                    <p className="text-xs text-gray-500">Erforderlich für Consent und Staging-Schutz.</p>
                  </div>
                  <input type="checkbox" checked readOnly data-testid="consent-essential-toggle" />
                </div>
              </label>
              <label className="rounded-xl border p-4 bg-gray-50 cursor-pointer">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0A2A43]">Statistik</p>
                    <p className="text-xs text-gray-500">Nur nach Zustimmung laden.</p>
                  </div>
                  <input type="checkbox" checked={draft.statistics} onChange={(e) => setDraft({ ...draft, statistics: e.target.checked })} data-testid="consent-statistics-toggle" />
                </div>
              </label>
              <label className="rounded-xl border p-4 bg-gray-50 cursor-pointer">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0A2A43]">Marketing</p>
                    <p className="text-xs text-gray-500">Steuert externe Inhalte wie Karten.</p>
                  </div>
                  <input type="checkbox" checked={draft.marketing} onChange={(e) => setDraft({ ...draft, marketing: e.target.checked })} data-testid="consent-marketing-toggle" />
                </div>
              </label>
              <div className="md:col-span-3 flex justify-end">
                <button type="button" onClick={() => saveConsent(draft)} className="px-4 py-2 rounded-lg bg-[#0A2A43] text-white text-sm hover:bg-[#0A2A43]/90" data-testid="consent-save-button">Auswahl speichern</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
